/**
 * The "meet Shannon" introduction shown before handing a traveler off to support (ELARA-750):
 * who she is, what she can help with, then the two ways to reach her — WhatsApp, or email for
 * travelers who don't use WhatsApp.
 *
 * Shared by every support entry point — the nav launcher ({@link SupportButton}) and the trips
 * header's advisor chip ({@link AdvisorChip}) — so support always introduces itself the same way.
 * The dialog owns the handoffs itself (via {@link useOpenSupportChat} / {@link useOpenSupportEmail})
 * rather than taking them as props, so call sites only manage whether it is open.
 */
import type { IconSize } from '#ui/MaterialIcon.js';

import { cn } from '@elara/client-core/lib/utils';

import { WhatsAppIcon } from '#ui/icons/whatsapp-icon.js';
import { AppImage, Button, Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '#ui/index.js';
import { IconSizes, MaterialIcon } from '#ui/MaterialIcon.js';
import { useOpenSupportChat, useOpenSupportEmail } from '#views/trip/use-support-chat.js';

/** Shannon's portrait, at an icon size or an explicit pixel size. */
export function ShannonAvatar({ size, className }: { size?: IconSize | number; className?: string }) {
  const pixelSize = typeof size === 'number' ? size : IconSizes[size ?? 'sm'];
  return (
    <AppImage
      src="/team/shannon.jpg"
      alt="Shannon"
      width={pixelSize}
      height={pixelSize}
      className={cn('rounded-full object-cover', className)}
      style={{ width: pixelSize, height: pixelSize }}
    />
  );
}

export function ShannonIntroDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const openSupportChat = useOpenSupportChat();
  const openSupportEmail = useOpenSupportEmail();

  const handleChat = () => {
    openSupportChat();
    onOpenChange(false);
  };

  const handleEmail = () => {
    openSupportEmail();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-xs">
        <DialogHeader className="items-center text-center">
          <ShannonAvatar size={120} />
          <DialogTitle className="pr-0 mt-2 mb-0">Shannon Culler</DialogTitle>
          <DialogDescription>Your Elara travel advisor</DialogDescription>
        </DialogHeader>

        <div className="text-sm text-center">
          <p>
            Shannon can help with trip planning, booking questions, and support if you experience issues while on your
            trip.
          </p>
        </div>

        {/* Equal halves, but not equal weight: WhatsApp keeps the filled treatment as the primary
            channel, email takes the outline as the alternative for travelers who don't use it. */}
        <div className="flex items-center gap-2 mt-3">
          <Button onClick={handleChat} className="gap-2 flex-1">
            <WhatsAppIcon className="h-4 w-4" />
            WhatsApp
          </Button>
          <Button variant="outline" onClick={handleEmail} className="gap-2 flex-1">
            <MaterialIcon icon="mail" size="sm" />
            Email
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
